import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Settings } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { authService } from '../../../services';
import { tenantService } from '../../../services/tenantService';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import CreateTenantModal from './CreateTenantModal';
import { Table } from '../../../components/ui/Table';

export const TenantListing: React.FC = () => {
  const navigate = useNavigate();
  const { setTokenAndRefresh } = useAuth();

  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loadingLoginAs, setLoadingLoginAs] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const res: any = await tenantService.list();
      setTenants(res?.tenants || []);
    } catch (err) {
      console.error('Failed to load tenants', err);
    } finally {
      setLoading(false);
    }
  };

  const loginAs = async (userId: string) => {
    if (loadingLoginAs) {
      toast.success('Already logging in as another tenant. Please wait.');
      return;
    }
    try {
      setLoadingLoginAs(userId);
      const data = await authService.loginAs({ userId });
      await setTokenAndRefresh(data);
    } catch (err) {
      console.error('Login as tenant failed', err);
    } finally {
      setLoadingLoginAs(null);
    }
  };

  const filteredTenants = useMemo(
    () => tenants.filter((t) => t.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [tenants, searchTerm]
  );

  return (
    <Page contentClassName="py-6">
      <PageHeader
        className="py-0"
        title="Tenant Listing"
        subtitle="All tenants in the system."
        aside={
          <Button tone="primary"
            onClick={() => setCreateOpen(true)}
            className="rounded-lg px-4 py-2 text-sm"
          >
            Create tenant
          </Button>
        }
      />

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={loadTenants}
      />

      <Section className="py-6">
        <Card className="mb-6">
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-between">
            <Input
              type="text"
              placeholder="Search tenants by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="md:max-w-lg"
            />

            <div className="flex rounded-lg bg-agentic-gray-100 p-1">
              <Button tone="custom"
                onClick={() => setViewMode('grid')}
                className={`rounded-lg px-4 py-2 text-sm ${viewMode === 'grid' ? 'bg-white shadow text-agentic-text-primary' : 'text-agentic-text-secondary'}`}
              >
                Grid
              </Button>
              <Button tone="custom"
                onClick={() => setViewMode('list')}
                className={`rounded-lg px-4 py-2 text-sm ${viewMode === 'list' ? 'bg-white shadow text-agentic-text-primary' : 'text-agentic-text-secondary'}`}
              >
                List
              </Button>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="py-12 text-center text-agentic-text-secondary">Loading tenants...</div>
        ) : filteredTenants.length === 0 ? (
          <div className="py-12 text-center text-agentic-text-secondary">No tenants found.</div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredTenants.map((tenant) => (
              <Card key={tenant.id}>
                <h3 className="text-lg font-semibold text-agentic-text-primary">{tenant.name}</h3>
                <p className="text-sm text-agentic-text-secondary">{tenant.slug}</p>

                <div className="mt-3 space-y-1 text-sm text-agentic-text-primary">
                  <div>
                    <strong>Total Users:</strong> {tenant.count}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    <span className={`rounded px-2 py-1 text-xs ${tenant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {tenant.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-agentic-text-secondary">
                    Created: {new Date(tenant.createdAt).toLocaleString()}
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={loadingLoginAs === tenant.adminUser.id}
                    onClick={() => loginAs(tenant.adminUser.id)}
                  >
                    <LogIn className="h-4 w-4" />
                    Login As
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/tenant/${tenant.id}/config`)}
                  >
                    <Settings className="h-4 w-4" />
                    Configure
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="overflow-x-auto p-0">
            <Table className="w-full min-w-[900px]">
              <thead className="bg-agentic-gray-100 text-left text-sm text-agentic-text-secondary">
                <tr>
                  <th className="p-4">Name</th>
                  <th className="p-4">Slug</th>
                  <th className="p-4">Total users</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Created At</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="border-t border-agentic-gray-200 hover:bg-agentic-gray-50">
                    <td className="p-4 font-medium text-agentic-text-primary">{tenant.name}</td>
                    <td className="p-4 text-agentic-text-secondary">{tenant.slug}</td>
                    <td className="p-4 text-agentic-text-primary">{tenant.count}</td>
                    <td className="p-4">
                      <span className={`rounded px-2 py-1 text-xs ${tenant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {tenant.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 text-agentic-text-secondary">{new Date(tenant.createdAt).toLocaleString()}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={loadingLoginAs === tenant.adminUser.id}
                          onClick={() => loginAs(tenant.adminUser.id)}
                        >
                          <LogIn className="h-4 w-4" />
                          Login As
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/tenant/${tenant.id}/config`)}
                        >
                          <Settings className="h-4 w-4" />
                          Configure
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </Section>
    </Page>
  );
};

export default TenantListing;
